import { useEffect } from "react";
import { SITE_URL } from "./api";

const SITE_NAME = "ArticleBlogs";
const DEFAULT_IMAGE = `${SITE_URL}/logo.svg`;

function setMeta(attr, key, content) {
  if (content == null) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function useSeo({ title, description, path, image, type = "website", robots = "index,follow", jsonLd = [], canonical }) {
  useEffect(() => {
    const fullTitle = title ? (title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`) : `${SITE_NAME} — Everyday Tips for Smarter Living`;
    const url = canonical || `${SITE_URL}${path || window.location.pathname}`;
    document.title = fullTitle;
    setMeta("name", "description", description || "");
    setMeta("name", "robots", robots);
    setCanonical(url);
    setMeta("property", "og:site_name", SITE_NAME);
    setMeta("property", "og:type", type);
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", description || "");
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", image || DEFAULT_IMAGE);
    setMeta("name", "twitter:card", image ? "summary_large_image" : "summary");
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", description || "");
    setMeta("name", "twitter:image", image || DEFAULT_IMAGE);

    document.querySelectorAll('script[data-seo-jsonld]').forEach((s) => s.remove());
    jsonLd.filter(Boolean).forEach((obj) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-seo-jsonld", "1");
      s.text = JSON.stringify(obj);
      document.head.appendChild(s);
    });
  }, [title, description, path, image, type, robots, canonical, JSON.stringify(jsonLd)]); // eslint-disable-line react-hooks/exhaustive-deps
}

export const orgJsonLd = () => ({
  "@context": "https://schema.org", "@type": "Organization", name: SITE_NAME, url: SITE_URL, logo: DEFAULT_IMAGE,
  description: "Practical ideas for spending less, wasting less, shopping smarter and living better.",
});

export const websiteJsonLd = () => ({
  "@context": "https://schema.org", "@type": "WebSite", name: SITE_NAME, url: SITE_URL,
  potentialAction: { "@type": "SearchAction", target: `${SITE_URL}/search/?q={search_term_string}`, "query-input": "required name=search_term_string" },
});

export const breadcrumbJsonLd = (items) => ({
  "@context": "https://schema.org", "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({ "@type": "ListItem", position: i + 1, name: it.name, item: `${SITE_URL}${it.path}` })),
});

export const articleJsonLd = (a, author) => ({
  "@context": "https://schema.org", "@type": a.schema_type || "Article", headline: a.title, description: a.seo_description || a.excerpt,
  image: a.featured_image ? [a.featured_image] : undefined, datePublished: a.published_at, dateModified: a.updated_at || a.published_at,
  author: { "@type": "Organization", name: author?.name || a.author_name, url: `${SITE_URL}/author/${a.author_slug}/` },
  publisher: { "@type": "Organization", name: SITE_NAME, logo: { "@type": "ImageObject", url: DEFAULT_IMAGE } },
  mainEntityOfPage: `${SITE_URL}/${a.category_slug}/${a.slug}/`,
});

export const faqJsonLd = (faqs) => faqs?.length ? ({
  "@context": "https://schema.org", "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.question, acceptedAnswer: { "@type": "Answer", text: f.answer } })),
}) : null;
